"use client";

import { format } from "date-fns";
import { CalendarCheck, ChevronRight, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { listTaggableVenueBookings } from "@/actions/activity-session-edit";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { formatEventDate } from "@/lib/dates/event-display";

type Booking = {
  id: number;
  startTime: Date;
  endTime: Date;
  venueName: string;
};

export function SessionBookingSelector({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (bookingId: number | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [bookings, setBookings] = useState<Booking[] | null>(null);
  const selected =
    value == null ? null : bookings?.find((booking) => booking.id === value);

  const load = async () => {
    setOpen(true);
    if (bookings != null) return;
    const result = await listTaggableVenueBookings();
    if (!result.ok) {
      toast.error(result.message);
      setBookings([]);
      return;
    }
    setBookings(result.data);
  };

  return (
    <>
      {value != null ? (
        <div className="flex items-center gap-3 px-4 py-3">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-success/10 text-success">
            <CalendarCheck className="size-4" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-success">
              Court confirmed
            </p>
            <p className="truncate text-xs text-text-tertiary">
              {selected
                ? `${selected.venueName} · ${format(
                    new Date(selected.startTime),
                    "EEE d MMM, h:mm a",
                  )}`
                : "Attached when you publish."}
            </p>
          </div>
          <button
            type="button"
            onClick={() => onChange(null)}
            aria-label="Remove court booking"
            className="flex size-8 shrink-0 items-center justify-center rounded-full text-text-tertiary transition-colors pointer-fine:hover:bg-surface-alt pointer-fine:hover:text-text-primary"
          >
            <X className="size-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={load}
          className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors pointer-fine:hover:bg-surface-alt/60 dark:pointer-fine:hover:bg-white/[0.04]"
        >
          <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-surface-alt text-text-secondary dark:bg-white/[0.08]">
            <CalendarCheck className="size-4" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-text-primary">
              Attach a court booking
            </span>
            <span className="block text-xs text-text-tertiary">
              Shows a “Court confirmed” badge
            </span>
          </span>
          <ChevronRight className="size-4 shrink-0 text-text-tertiary" />
        </button>
      )}

      <Drawer open={open} onOpenChange={setOpen} nested>
        <DrawerContent
          className="z-[110] max-h-[80dvh]"
          overlayClassName="z-[108]"
        >
          <DrawerHeader>
            <DrawerTitle>Attach a court booking</DrawerTitle>
            <DrawerDescription>
              Players see a “Court confirmed” badge. The booking price remains
              private and does not change the split.
            </DrawerDescription>
          </DrawerHeader>
          <div className="space-y-2 overflow-y-auto px-4 pb-[calc(env(safe-area-inset-bottom)+1.5rem)]">
            {bookings == null ? (
              <p className="py-4 text-center text-sm text-text-tertiary">
                Loading your bookings…
              </p>
            ) : bookings.length === 0 ? (
              <p className="rounded-xl border border-border bg-surface-alt/50 p-4 text-sm text-text-tertiary">
                You have no confirmed upcoming court bookings to attach.
              </p>
            ) : (
              bookings.map((booking) => (
                <button
                  key={booking.id}
                  type="button"
                  onClick={() => {
                    onChange(booking.id);
                    setOpen(false);
                  }}
                  className="w-full rounded-xl border border-border bg-surface p-3 text-left transition-colors hover:bg-surface-alt/60"
                >
                  <span className="block text-sm font-medium text-text-primary">
                    {booking.venueName}
                  </span>
                  <span className="mt-0.5 block text-xs text-text-tertiary">
                    {formatEventDate(
                      new Date(booking.startTime),
                      "EEE d MMM, h:mm a",
                    )}{" "}
                    – {formatEventDate(new Date(booking.endTime), "h:mm a")}
                  </span>
                </button>
              ))
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}
